import type { FC } from 'react';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { ExpandMore } from '@mui/icons-material';
import { Box } from '@mui/material';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@src/design-system/collapsible';
import type { TAddress } from '@shared/types/organization';

const AddressInfo: FC<{ address: TAddress }> = ({ address }) => {
  const [open, setOpen] = useState(true);

  return (
    <Box
      className='w-full border border-primary-200 rounded-lg py-3 px-3 mt-4'
      sx={{ bgcolor: '#fdfdfe' }}
    >
      <Collapsible
        open={open}
        onOpenChange={setOpen}
      >
        <CollapsibleTrigger asChild>
          <button className='flex items-center justify-between w-full text-lg font-semibold'>
            <p className='text-gray-800 text-base font-medium flex items-center gap-1'>
              Address Information
            </p>
            <motion.div
              animate={{ rotate: open ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ExpandMore className='w-6 h-6' />
            </motion.div>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent className='overflow-hidden mt-4'>
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: open ? 1 : 0, height: open ? 'auto' : 0 }}
            transition={{ duration: 0.3 }}
          >
            <p className='text-gray-600 text-sm mb-1'>Address 1</p>
            <p className='text-gray-800 font-medium text-sm mb-3'>
              {address?.address_line_1 || '-'}
            </p>

            <p className='text-gray-600 text-sm mb-1'>Address 2</p>
            <p className='text-gray-800 font-medium text-sm mb-3'>
              {address?.address_line_2 || '-'}
            </p>

            <p className='text-gray-600 text-sm mb-1'>City</p>
            <p className='text-gray-800 font-medium text-sm mb-3'>
              {address?.city || '-'}
            </p>

            <p className='text-gray-600 text-sm mb-1'>State/Province</p>
            <p className='text-gray-800 font-medium text-sm mb-3'>
              {address?.state || '-'}
            </p>

            <p className='text-gray-600 text-sm mb-1'>ZIP/Postal Code</p>
            <p className='text-gray-800 font-medium text-sm mb-3'>
              {address?.postal_code || '-'}
            </p>

            <p className='text-gray-600 text-sm mb-1'>Country</p>
            <p className='text-gray-800 font-medium text-sm mb-3'>
              {typeof address?.country === 'string'
                ? address?.country
                : address?.country.name || '-'}
            </p>
          </motion.div>
        </CollapsibleContent>
      </Collapsible>
    </Box>
  );
};

export default AddressInfo;
